import { Table, TableBody, TableContainer } from '@mui/material';

import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { useTablePagination } from '../../../hooks';
import { Button, Loader, Pagination, TableHead } from '../../Shared';
import { TableRow } from './TableRow';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_DATA_LOADING: 'isDataLoading',
  IS_DATA_ERROR: 'isDataError',
};

const CELLS = [
  { id: 'tenantName', label: 'Client', width: '150px' },
  { id: 'fileName', label: 'File', width: '178px' },
  { id: 'exportDate', label: 'Export date', width: '110px' },
  { id: 'uploadedOn', label: 'Uploaded', width: '116px' },
  { id: 'rowsTotal', label: 'Rows', align: 'right', width: '72px' },
  {
    id: 'rowsMatched',
    label: 'Matched to leads',
    align: 'right',
    width: '140px',
  },
  {
    id: 'rowsWithoutLead',
    label: 'Without a lead',
    align: 'right',
    width: '122px',
  },
  { id: 'actions', label: '', width: '56px' },
];

const getState = (data, isDataLoading, isDataError) => {
  if (isDataLoading) return STATE.IS_DATA_LOADING;
  if (isDataError) return STATE.IS_DATA_ERROR;
  if (data.length === 0) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

export const UploadsTable = ({
  uploads,
  isFiltersChanged,
  isUploadsLoading,
  isUploadsError,
  fetchUploads,
  onOpenReport,
  onOpenDelete,
}) => {
  const { page, count, rowsByPage, counter, setPage } =
    useTablePagination(uploads);

  const state = getState(uploads, isUploadsLoading, isUploadsError);

  return (
    <>
      {(state === STATE.HAS_DATA || state === STATE.HAS_NO_DATA) && (
        <>
          <TableContainer
            className={state === STATE.HAS_DATA ? 'h-full' : undefined}
          >
            <Table stickyHeader>
              <TableHead isCheckboxVisible={false} cells={CELLS} />
              {state === STATE.HAS_DATA && (
                <TableBody>
                  {rowsByPage.map((upload, idx) => (
                    <TableRow
                      key={upload.importId}
                      upload={upload}
                      withBorderBottom={rowsByPage.length !== idx + 1}
                      onOpenReport={onOpenReport}
                      onOpenDelete={onOpenDelete}
                    />
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
          {state === STATE.HAS_DATA && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-base-800 text-[14px]/[20px]">
                {counter}
              </span>
              <Pagination count={count} page={page} onChange={setPage} />
            </div>
          )}
          {state === STATE.HAS_NO_DATA && (
            <div className="flex justify-center pt-2 px-6 pb-6">
              <span className="text-base-800 text-[16px]/[24px]">
                {isFiltersChanged
                  ? 'No uploads match the filters'
                  : 'No uploads yet'}
              </span>
            </div>
          )}
          <p className="text-base-800 text-[14px]/[20px]">
            Upload the export after the verified leads are imported in Access.
            Only rows that match a lead of the client are saved; upload again if
            more leads are imported later. A newer upload for the same lead
            replaces the data the briefs use; older versions are kept.
          </p>
        </>
      )}
      {state === STATE.IS_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
      {state === STATE.IS_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load records
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={fetchUploads}
          />
        </div>
      )}
    </>
  );
};
